import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Check, Phone } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { CTABand } from "@/components/site/CTABand";
import { services, reasons, sectors, accreditations, testimonials, stats, company, IMAGES } from "@/data/content";

const heroLines = ["Total Facilities", "Services for", "modern Britain."];

const Hero = () => (
  <section data-testid="hero" className="relative min-h-screen flex flex-col overflow-hidden bg-ink grain">
    <motion.div
      className="absolute inset-0 z-0"
      initial={{ scale: 1.15 }}
      animate={{ scale: 1 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <img src={IMAGES.heroBuilding} alt="Commercial building" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-ink/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-transparent to-transparent" />
    </motion.div>

    <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(ellipse_60%_50%_at_20%_15%,rgba(43,91,132,0.35),transparent_60%)]" />
    <div className="absolute top-24 -left-40 z-[1] h-[520px] w-[520px] rounded-full bg-brand/10 blur-[140px] pointer-events-none" />

    <div className="relative z-10 flex-1 flex items-center w-full">
      <div className="max-w-container mx-auto px-6 md:px-12 w-full pt-32 pb-12">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
        <Overline>Nationwide facilities management</Overline>
      </motion.div>

      <h1 className="mt-8 font-display font-semibold text-white text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
        {heroLines.map((line, i) => (
          <span key={i} className="block overflow-hidden">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        className="mt-8 text-lg md:text-xl text-slatetxt max-w-2xl leading-relaxed"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.8 }}
      >
        Security, cleaning, maintenance and more — delivered under one accountable contract
        for commercial properties, residential developments, managing agents and construction sites.
      </motion.p>

      <motion.div
        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.05, duration: 0.8 }}
      >
        <Link
          to="/quote"
          data-testid="hero-quote-button"
          className="inline-flex items-center gap-3 bg-brand hover:bg-brand-hover text-white font-semibold px-8 py-4 transition-colors duration-300 group"
        >
          Request a Quote
          <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
        <a
          href={`tel:${company.phone.replace(/\s/g, "")}`}
          data-testid="hero-call-button"
          className="inline-flex items-center gap-3 border border-border hover:border-white/60 text-white font-medium px-8 py-4 transition-colors duration-300"
        >
          <Phone size={18} /> {company.phone}
        </a>
      </motion.div>
      </div>
    </div>

    <motion.div
      className="relative z-10 border-t border-white/10 bg-ink/40 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2, duration: 0.8 }}
    >
      <div className="max-w-container mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
        {stats.map((s) => (
          <div key={s.label} className="py-6 px-2 md:px-6 first:pl-0">
            <div className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight">{s.value}</div>
            <div className="text-xs md:text-sm text-slatetxt mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </motion.div>
  </section>
);

const Intro = () => (
  <section data-testid="intro" className="bg-ink py-24 md:py-32">
    <div className="max-w-container mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-4">
        <Reveal><Overline>Who we are</Overline></Reveal>
      </div>
      <div className="md:col-span-8">
        <Reveal delay={0.05}>
          <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-white leading-[1.1]">
            A single, trusted partner for every service your buildings depend on.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-lg text-slatetxt leading-relaxed max-w-3xl">
            Total Facilities Services delivers integrated facilities management across the UK. From SIA-licensed
            security and immaculate commercial cleaning to 24/7 reactive maintenance, waste, parking and fire
            safety — we consolidate it all into one accountable contract, backed by a control room that never sleeps.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-white font-medium hover:text-brand-light transition-colors group">
            More about TFS <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-4", "md:col-span-4", "md:col-span-4"];

const ServiceCard = ({ s, span, i }) => {
  const Icon = s.icon;
  return (
    <Reveal delay={(i % 3) * 0.06} className={span}>
      <Link
        to="/services"
        data-testid={`home-service-${s.slug}`}
        className="group relative block h-full bg-surface border border-border hover:border-brand/50 transition-colors duration-300 overflow-hidden"
      >
        <div className="p-8 md:p-10 flex flex-col h-full min-h-[220px]">
          <Icon size={40} className="text-brand-light" strokeWidth={1.5} />
          <h3 className="mt-6 font-display font-medium text-2xl text-white tracking-tight group-hover:text-brand-light transition-colors">
            {s.title}
          </h3>
          <p className="mt-3 text-slatetxt leading-relaxed">{s.short}</p>
          <div className="mt-auto pt-6 flex items-center gap-2 text-sm text-white/70 group-hover:text-white transition-colors">
            Explore service
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </div>
      </Link>
    </Reveal>
  );
};

const ServicesGrid = () => (
  <section data-testid="services-grid" className="bg-ink py-24 md:py-32 border-t border-border">
    <div className="max-w-container mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Reveal><Overline>What we do</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl tracking-tighter text-white leading-[1]">
              Seven services.<br /> One partner.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <Link to="/services" className="inline-flex items-center gap-2 text-white font-medium hover:text-brand-light transition-colors group whitespace-nowrap">
            View all services <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        {services.map((s, i) => (
          <ServiceCard key={s.slug} s={s} span={spans[i]} i={i} />
        ))}
      </div>
    </div>
  </section>
);

const Reasons = () => (
  <section data-testid="reasons" className="bg-surface py-24 md:py-32">
    <div className="max-w-container mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
        <div className="md:col-span-4">
          <Reveal><Overline>Why choose TFS</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl tracking-tighter text-white leading-[1.05]">
              Established. Accountable. Everywhere you need us.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 relative aspect-[4/5] overflow-hidden border border-border">
              <img src={IMAGES.security} alt="Security officer" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-8 md:pl-8">
          <div className="divide-y divide-border">
            {reasons.map((r, i) => (
              <Reveal key={r.n} delay={i * 0.05}>
                <div className="relative py-10 group">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    <div className="md:col-span-2 font-display font-black text-6xl text-border group-hover:text-brand/40 transition-colors duration-500 leading-none">
                      {r.n}
                    </div>
                    <div className="md:col-span-10">
                      <h3 className="font-display font-medium text-2xl md:text-3xl text-white tracking-tight">{r.title}</h3>
                      <p className="mt-3 text-slatetxt text-lg leading-relaxed">{r.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const SectorsStrip = () => (
  <section data-testid="sectors-strip" className="relative bg-ink py-24 md:py-32 border-t border-border overflow-hidden">
    <div className="max-w-container mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end mb-14">
        <div className="md:col-span-8">
          <Reveal><Overline>Sectors we support</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl tracking-tighter text-white leading-[1]">
              Trusted across every kind of estate.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Reveal delay={0.1}>
            <Link to="/sectors" className="inline-flex items-center gap-2 text-white font-medium hover:text-brand-light transition-colors group">
              All sectors <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sectors.map((sec, i) => {
          const Icon = sec.icon;
          return (
            <Reveal key={sec.title} delay={(i % 4) * 0.05}>
              <div className="group bg-surface border border-border hover:border-brand/50 p-7 h-full transition-colors duration-300">
                <Icon size={30} className="text-brand-light" strokeWidth={1.5} />
                <h3 className="mt-5 font-display font-medium text-xl text-white tracking-tight">{sec.title}</h3>
                <p className="mt-2 text-sm text-slatetxt leading-relaxed">{sec.blurb}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const AccreditationsMarquee = () => (
  <section data-testid="accreditations-marquee" className="bg-surface py-20 border-y border-border">
    <div className="max-w-container mx-auto px-6 md:px-12 mb-10">
      <Reveal>
        <div className="flex items-center gap-4">
          <Overline>Accreditations & compliance</Overline>
        </div>
      </Reveal>
    </div>
    <Marquee speed={40} gradient gradientColor="#0B132B" gradientWidth={120} autoFill>
      {accreditations.map((a) => (
        <div key={a.name} className="mx-8 md:mx-14 flex items-center gap-4">
          <span className="h-2 w-2 bg-brand rotate-45" />
          <div>
            <div className="font-display font-semibold text-xl md:text-2xl text-white/80 tracking-tight whitespace-nowrap">{a.name}</div>
            <div className="text-xs text-slatetxt uppercase tracking-[0.15em] whitespace-nowrap">{a.note}</div>
          </div>
        </div>
      ))}
    </Marquee>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials" className="bg-ink py-24 md:py-32">
    <div className="max-w-container mx-auto px-6 md:px-12">
      <Reveal><Overline>What clients say</Overline></Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <figure className="h-full bg-surface border border-border p-8 md:p-10 flex flex-col">
              <div className="font-display text-6xl text-brand/40 leading-none">"</div>
              <blockquote className="mt-2 text-white text-lg leading-relaxed">{t.quote}</blockquote>
              <figcaption className="mt-8 pt-6 border-t border-border">
                <div className="font-semibold text-white">{t.name}</div>
                <div className="text-sm text-slatetxt">{t.role}</div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div>
      <Hero />
      <Intro />
      <ServicesGrid />
      <Reasons />
      <SectorsStrip />
      <AccreditationsMarquee />
      <Testimonials />
      <CTABand />
    </div>
  );
}
